import { convertImageToPng, dataURLtoFile } from '../../utils/generalHelpers';
import { CameraIcon, WarningIcon } from '../../assets/icons/common';
import { uploadFileThunk } from '../../store/general/thunks';
import { setNotification } from '../../store/general/slice';
import { ProfileData } from '../../store/workspace/types';
import { kycThunk } from '../../store/exam/thunks';
import { useWebcam } from '../../hooks/useWebcam';
import { Logo } from '../../assets/icons/common';
import { useDispatch } from '../../store/store';
import Spinner from '../../components/Spinner';
import { useNavigate } from 'react-router-dom';
import MotionWrapper from '../MotionWrapper';
import Button from '../../components/Button';
import styles from './style.module.scss';
import Webcam from 'react-webcam';
import { useState } from 'react';
import DropBox from '../DropBox';

type IdImageMode = 'Camera' | 'Browse';
type Step = 'Face' | 'ID' | 'Submission';
const rejectText = "Your KYC didn't approved!";
type SubmissionStatus = 'Pending' | 'Approved' | 'Rejected';
const idSample = require('../../assets/images/kyc_id.png');
const faceSample = require('../../assets/images/kyc_personal.png');

const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user"
};

interface Props {
    jobId: string | null;
    userId: string | null;
    examId: string | null;
    onSuccess: () => void;
    profile: ProfileData | null;
};

export default function KYC(props: Props) {
    const {
        jobId,
        userId,
        examId,
        profile,
        onSuccess,
    } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('Face');
    const [converting, setConverting] = useState(false);
    const [idImgSrc, setIdImgSrc] = useState<string | null>(null);
    const [idImgFile, setIdImgFile] = useState<File | null>(null);
    const [faceImgSrc, setFaceImgSrc] = useState<string | null>(null);
    const [idImageMode, setIdImageMode] = useState<IdImageMode>('Camera');
    const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('Pending');
    const t = idImgFile ? URL.createObjectURL(idImgFile) : '';

    const {
        imgSrc,
        webcamRef,
        setImgSrc,
        captureImage,
    } = useWebcam();

    const takePhoto = () => {
        if (imgSrc) {
            setImgSrc(null);
        } else {
            captureImage();
        }
    };

    const saveFace = () => {
        if (!profile) return;
        setFaceImgSrc(() => imgSrc);

        if (profile?.kyc === 'approved') {
            setStep('Submission');
            submit(imgSrc, null);
            setImgSrc(null);
        } else {
            setImgSrc(null);
            setStep('ID');
        }
    };

    const saveID = async () => {
        setIdImgSrc(imgSrc);

        if (idImageMode === 'Browse') {
            if (!idImgFile) return;
            setConverting(true);
            convertImageToPng(idImgFile)
                .then(dataUrl => {
                    setConverting(false);
                    setStep('Submission');
                    submit(faceImgSrc, dataUrl);
                })
                .catch(() => {
                    setConverting(false);
                    dispatch(setNotification(['Failed to convert image.', 'error']));
                })
        } else {
            setStep('Submission');
            submit(faceImgSrc, imgSrc);
            setImgSrc(null);
        }
    };

    const startOver = () => {
        setStep('Face');
        setIdImgSrc(null);
        setFaceImgSrc(null);
        setSubmissionStatus('Pending');
    };

    const changeIdImageMode = () => {
        setImgSrc(null);
        setIdImgFile(null);
        setIdImageMode(prev => prev === 'Camera' ? 'Browse' : 'Camera');
    };

    const submit = async (faceImgSrc: string | null, idImgSrc: string | null) => {
        if (!profile || !jobId || !examId || !faceImgSrc) return;
        setSubmissionStatus('Pending');
        if (profile.kyc === 'approved') {
            const faceImgFile = dataURLtoFile(faceImgSrc, 'face.png');
            const personalPhotoResponse = await dispatch(uploadFileThunk({
                fileName: `${userId}/${jobId}/${examId}/face.png`,
                file: faceImgFile
            }));

            const etag1 = personalPhotoResponse.payload;

            await dispatch(kycThunk({
                user_id: userId,
                e_tags: [etag1]
            }))
                .unwrap()
                .then(() => {
                    onSuccess();
                })
                .catch(() => {
                    setSubmissionStatus('Rejected');
                })
        } else {
            if (!idImgSrc) return;
            const PIF = dataURLtoFile(faceImgSrc, 'face.png');
            const IDF = dataURLtoFile(idImgSrc, 'id.png');
            const PIF_eTag = (await dispatch(uploadFileThunk({ fileName: `${userId}/face.png`, file: PIF }))).payload;
            const IDF_eTag = (await dispatch(uploadFileThunk({ fileName: `${userId}/id.png`, file: IDF }))).payload;
            await dispatch(uploadFileThunk({ fileName: `${userId}/${jobId}/${examId}/face.png`, file: PIF }));

            dispatch(kycThunk({
                e_tags: [PIF_eTag, IDF_eTag]
            }))
                .unwrap()
                .then(() => {
                    onSuccess();
                })
                .catch(() => {
                    setSubmissionStatus('Rejected');
                })
        }
    };

    return (
        <MotionWrapper className={styles.container}>
            {step === 'Face' && <div className={styles.face}>
                <div className={styles.header}>
                    <span className={styles.header_title}>
                        Please take a live photo from yourself
                    </span>
                </div>
                <div className={styles.sample}>
                    <img src={faceSample} alt="sample" />
                </div>
                <div className={styles.camera}>
                    {!imgSrc ? <Webcam
                        height={720}
                        width={1280}
                        audio={false}
                        ref={webcamRef}
                        disablePictureInPicture
                        screenshotFormat="image/png"
                        videoConstraints={videoConstraints}
                    /> : <img src={imgSrc} />}
                </div>
                <div className={styles.actions}>
                    <Button
                        onClick={takePhoto}
                        type='Outline-Primary'
                        className={styles.actions_btn}
                        text={imgSrc ? 'Retake photo' : 'Take photo'}
                    />
                    {imgSrc && <Button
                        type='Primary'
                        onClick={saveFace}
                        text='Save and continue'
                        className={styles.actions_btn}
                    />}
                </div>
                <span
                    className={styles.back}
                    onClick={() => navigate(-1)}
                >
                    Return to job description
                </span>
            </div>}

            {step === 'ID' && <div className={styles.id}>
                <div className={styles.header}>
                    <span className={styles.header_title}>Please take a live photo from your ID</span>
                    <span className={styles.header_text}>Drivers license or Passport</span>
                </div>
                <div className={styles.sample}>
                    <img src={idSample} alt="sample" />
                </div>
                {idImageMode === 'Camera' && <>
                    <div className={styles.camera}>
                        {!imgSrc ? <Webcam
                            height={720}
                            width={1280}
                            audio={false}
                            ref={webcamRef}
                            disablePictureInPicture
                            screenshotFormat="image/png"
                            videoConstraints={videoConstraints}
                        /> : <img src={imgSrc} />}
                    </div>
                    <div className={styles.actions}>
                        <Button
                            onClick={takePhoto}
                            type='Outline-Primary'
                            className={styles.actions_btn}
                            text={imgSrc ? 'Retake photo' : 'Take photo'}
                        />
                        {imgSrc && <Button
                            type='Primary'
                            onClick={saveID}
                            text='Save and continue'
                            className={styles.actions_btn}
                        />}
                    </div>
                </>}

                {idImageMode === 'Browse' && <>
                    <div className={styles.upload_box}>
                        <div className={styles.upload_box_preview}>
                            {idImgFile ? <img src={t} /> :
                                <div className={styles.upload_box_preview_empty}>
                                    <CameraIcon />
                                    <span>Preview</span>
                                </div>}
                        </div>
                        <DropBox
                            file={idImgFile}
                            uploading={false}
                            setFile={setIdImgFile}
                            fileTypes={{ 'image': ['.PNG', '.JPEG', '.JPG'], }}
                            description='PNG, JPEG or JPG , Maximum upload file size: 12MB'
                        />
                    </div>
                </>}

                <div className={styles.change_option}>
                    <span className={styles.change_option_text}>
                        {idImageMode === 'Camera' ? ' Also you can upload the relevent documents' : 'Also you can take a live photo of your ID'}
                    </span>
                    <span
                        className={styles.change_option_link}
                        onClick={changeIdImageMode}
                    >
                        {idImageMode === 'Camera' ? ' Upload document' : 'Take photo'}
                    </span>
                </div>

                {idImageMode === 'Browse' && <Button
                    onClick={saveID}
                    loading={converting}
                    disabled={!idImgFile}
                    type='Outline-Primary'
                    text='Save and continue'
                    className={styles.actions_btn}
                />}

                <span
                    className={styles.back}
                    onClick={() => setStep('Face')}
                >
                    Back to previous step
                </span>
            </div>}

            {step === 'Submission' && <div className={styles.submission}>
                <div className={styles.content}>
                    <Logo width={100} height={100} />
                    <h3>Meritocracy is our passion</h3>

                    {submissionStatus === 'Pending' && (<div className={styles.pending}>
                        <span>Wait for KYC approval</span>
                        <span className={styles.description}>It will take around 2 minutes or less</span>
                        <Spinner />
                    </div>)}

                    {submissionStatus === 'Rejected' && (<div className={styles.reject}>
                        <span>{rejectText}</span>
                        <div className={styles.reject_description}>
                            <div className={styles.reject_description_item}>
                                <WarningIcon />
                                <span>If the face is not clear, please try again.</span>
                            </div>
                            {profile?.kyc === 'notApproved' && <div className={styles.reject_description_item}>
                                <WarningIcon />
                                <span>The ID photo should be clear and bright, and the text shouldn't be blurry.</span>
                            </div>}
                        </div>
                        <Button
                            type='Primary'
                            text='Start over KYC'
                            onClick={startOver}
                        />
                    </div>)}
                </div>
            </div>}
        </MotionWrapper>
    )
};
